import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ScrollText, Plus, Edit, Trash2, Users, FileText, Upload, X, Eye, Download, Bell, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['General', 'Policy Update', 'HR Announcement', 'Operations', 'Compliance', 'Safety', 'Other'];

const formatDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const MemoManager = () => {
  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [readsDialog, setReadsDialog] = useState(null);
  const [readData, setReadData] = useState(null);
  const [readsTab, setReadsTab] = useState('pending'); // 'pending' | 'acknowledged'
  const [reminding, setReminding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/memos`);
      setMemos(r.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('Delete this memo? Acknowledgments will also be removed.')) return;
    await axios.delete(`${API}/api/memos/${id}`);
    toast({ title: 'Memo deleted' });
    load();
  };

  const openReads = async (memo) => {
    setReadsDialog(memo);
    setReadData(null);
    setReadsTab('pending');
    try {
      const r = await axios.get(`${API}/api/memos/${memo.id}/reads`);
      setReadData(r.data);
    } catch { setReadData({ reads: [], pending: [], total_read: 0, total_pending: 0 }); }
  };

  const sendReminder = async (employeeIds = null) => {
    if (!readsDialog) return;
    const targetCount = employeeIds ? employeeIds.length : (readData?.total_pending || 0);
    if (!targetCount) return;
    const msg = employeeIds && employeeIds.length === 1
      ? 'Send a reminder to this employee?'
      : `Send a reminder to ${targetCount} pending employee${targetCount === 1 ? '' : 's'}?`;
    if (!window.confirm(msg)) return;
    setReminding(true);
    try {
      const res = await axios.post(`${API}/api/memos/${readsDialog.id}/remind`, employeeIds ? { employee_ids: employeeIds } : {});
      toast({ title: 'Reminder sent', description: `${res.data.sent} employee${res.data.sent === 1 ? '' : 's'} notified.` });
    } catch (e) {
      toast({ title: 'Failed to send reminder', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setReminding(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="memo-manager-page">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ScrollText className="h-7 w-7 text-indigo-500" /> Company Memos
          </h1>
          <p className="text-gray-600 mt-1">Internal memos visible on every employee portal with acknowledgement</p>
        </div>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="memo-new-btn">
          <Plus className="h-4 w-4 mr-1" /> New Memo
        </Button>
      </div>

      {loading ? (
        <Card><CardContent className="py-12 text-center text-gray-500">Loading…</CardContent></Card>
      ) : memos.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <ScrollText className="h-12 w-12 mx-auto text-indigo-300 mb-3" />
            <p className="text-gray-600">No memos yet. Click <strong>New Memo</strong> to publish one.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="memo-list">
          {memos.map(m => (
            <Card key={m.id} className="border hover:shadow-md transition" data-testid={`memo-${m.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">{m.title}</CardTitle>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant="outline" className="text-xs">{m.category}</Badge>
                      <Badge className={m.status === 'published' ? 'bg-emerald-100 text-emerald-700 text-xs' : 'bg-slate-100 text-slate-600 text-xs'}>
                        {m.status}
                      </Badge>
                      <span className="text-xs text-gray-400">{formatDate(m.created_at)}</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {m.content && (
                  <div className="text-sm text-gray-600 line-clamp-3 mb-2" dangerouslySetInnerHTML={{ __html: m.content }} />
                )}
                {m.file_name && (
                  <div className="flex items-center gap-2 text-xs text-indigo-600 mb-3">
                    <FileText className="h-4 w-4" /> {m.file_name}
                  </div>
                )}
                <div className="flex items-center gap-2 pt-2 border-t flex-wrap">
                  <button
                    onClick={() => openReads(m)}
                    className="flex items-center gap-1 text-xs text-indigo-700 bg-indigo-50 px-2 py-1 rounded hover:bg-indigo-100"
                    data-testid={`memo-reads-${m.id}`}
                  >
                    <Users className="h-3 w-3" /> {m.read_count || 0} acknowledgement{(m.read_count || 0) === 1 ? '' : 's'}
                  </button>
                  {m.file_url && (
                    <a
                      href={`${API}${m.file_url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                    >
                      <Download className="h-3 w-3" /> File
                    </a>
                  )}
                  <div className="ml-auto flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(m); setShowForm(true); }} data-testid={`memo-edit-${m.id}`}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(m.id)} className="text-rose-600" data-testid={`memo-delete-${m.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showForm && (
        <MemoFormDialog
          memo={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {readsDialog && (
        <Dialog open onOpenChange={() => { setReadsDialog(null); setReadData(null); }}>
          <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-4 w-4" /> Acknowledgement Status — {readsDialog.title}
              </DialogTitle>
            </DialogHeader>
            {!readData ? (
              <div className="py-6 text-center text-gray-500 text-sm">Loading…</div>
            ) : (
              <>
                {/* Summary bar */}
                <div className="grid grid-cols-3 gap-2 mt-2 mb-3">
                  <div className="rounded-md border bg-emerald-50 px-3 py-2 text-center">
                    <p className="text-xl font-bold text-emerald-700" data-testid="reads-total-ack">{readData.total_read || 0}</p>
                    <p className="text-[11px] text-emerald-700">Acknowledged</p>
                  </div>
                  <div className="rounded-md border bg-amber-50 px-3 py-2 text-center">
                    <p className="text-xl font-bold text-amber-700" data-testid="reads-total-pending">{readData.total_pending || 0}</p>
                    <p className="text-[11px] text-amber-700">Pending</p>
                  </div>
                  <div className="rounded-md border bg-gray-50 px-3 py-2 text-center">
                    <p className="text-xl font-bold text-gray-700">{readData.total_audience || 0}</p>
                    <p className="text-[11px] text-gray-600">Total audience</p>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 bg-gray-100 rounded-md p-1 mb-3">
                  <button
                    onClick={() => setReadsTab('pending')}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium rounded ${readsTab === 'pending' ? 'bg-white shadow-sm text-amber-700' : 'text-gray-600'}`}
                    data-testid="reads-tab-pending"
                  >
                    Pending ({readData.total_pending || 0})
                  </button>
                  <button
                    onClick={() => setReadsTab('acknowledged')}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium rounded ${readsTab === 'acknowledged' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-600'}`}
                    data-testid="reads-tab-acknowledged"
                  >
                    Acknowledged ({readData.total_read || 0})
                  </button>
                </div>

                {/* Pending list */}
                {readsTab === 'pending' && (
                  <div>
                    {(readData.pending || []).length === 0 ? (
                      <div className="py-6 text-center text-emerald-600 text-sm">🎉 Everyone has acknowledged this memo.</div>
                    ) : (
                      <>
                        <div className="flex justify-end mb-2">
                          <Button
                            size="sm"
                            onClick={() => sendReminder(null)}
                            disabled={reminding}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="remind-all-btn"
                          >
                            {reminding ? (
                              <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Sending…</>
                            ) : (
                              <><Bell className="h-3.5 w-3.5 mr-1" /> Remind all pending</>
                            )}
                          </Button>
                        </div>
                        <div className="space-y-1 max-h-[40vh] overflow-y-auto">
                          {readData.pending.map((p) => (
                            <div key={p.employee_id} className="flex items-center justify-between text-sm border-b py-2" data-testid={`pending-row-${p.employee_code}`}>
                              <div>
                                <p className="font-semibold">{p.employee_name}</p>
                                <p className="text-xs text-gray-500">{p.employee_code} • {p.department}</p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => sendReminder([p.employee_id])}
                                disabled={reminding}
                                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                data-testid={`remind-single-${p.employee_code}`}
                              >
                                <Bell className="h-3.5 w-3.5 mr-1" /> Remind
                              </Button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Acknowledged list */}
                {readsTab === 'acknowledged' && (
                  <div>
                    {readData.reads.length === 0 ? (
                      <div className="py-6 text-center text-gray-500 text-sm">No employee has acknowledged yet.</div>
                    ) : (
                      <div className="space-y-1 max-h-[40vh] overflow-y-auto">
                        {readData.reads.map((r, i) => (
                          <div key={i} className="flex items-center justify-between text-sm border-b py-2">
                            <div>
                              <p className="font-semibold">{r.employee_name}</p>
                              <p className="text-xs text-gray-500">{r.employee_code} • {r.department}</p>
                            </div>
                            <span className="text-xs text-gray-500">{formatDate(r.read_at)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// ── Form Dialog ─────────────────────────────────────────────────

const MemoFormDialog = ({ memo, onClose, onSaved }) => {
  const editing = !!memo;
  const [title, setTitle] = useState(memo?.title || '');
  const [category, setCategory] = useState(memo?.category || 'General');
  const [content, setContent] = useState(memo?.content || '');
  const [status, setStatus] = useState(memo?.status || 'published');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!title.trim()) return toast({ title: 'Title required', variant: 'destructive' });
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('category', category);
      fd.append('content', content);
      fd.append('status', status);
      if (file) fd.append('file', file);
      if (editing) await axios.put(`${API}/api/memos/${memo.id}`, fd);
      else await axios.post(`${API}/api/memos`, fd);
      toast({ title: editing ? 'Memo updated' : 'Memo published' });
      onSaved();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="memo-form">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Memo' : 'New Memo'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} data-testid="memo-title" placeholder="e.g. New Leave Policy effective May 2026" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="memo-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="memo-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Published (visible on portal)</SelectItem>
                  <SelectItem value="draft">Draft (hidden)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Memo Body</Label>
            <Textarea
              rows={8}
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Write the full memo content here. HTML formatting is supported (you can paste from Word)."
              data-testid="memo-content"
            />
            <p className="text-[10px] text-gray-400 mt-1">Tip: paste rich text from Word/Docs — formatting is preserved.</p>
          </div>
          <div>
            <Label className="text-xs">Attachment (optional — PDF / JPG / DOCX)</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="memo-file"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                <Upload className="h-4 w-4 mr-1" /> Choose File
              </Button>
              {file ? (
                <span className="text-xs text-gray-700 flex items-center gap-1">
                  <FileText className="h-3 w-3" /> {file.name}
                  <button onClick={() => setFile(null)} className="text-rose-500"><X className="h-3 w-3" /></button>
                </span>
              ) : memo?.file_name ? (
                <span className="text-xs text-gray-500">Currently: {memo.file_name}</span>
              ) : (
                <span className="text-xs text-gray-400">No file selected</span>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="memo-save">
              {saving ? 'Saving…' : editing ? 'Update' : 'Publish'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MemoManager;
